import { Check, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { BaseRecipeDto } from "../../../shared/nutrition-addon";
import { api } from "../../api/client";
import { Alert, ICON } from "../../components/ui";
import { FoodSearch } from "../menus/FoodSearch";

// A base preparation (blat de pizza, sos demiglace…) asked about while completing a dish (owner 2026-11-02): the
// standard recipe is proposed from the food database; the restaurant confirms it or changes it into its own. Once
// saved it is used, with each dish's grams, everywhere the preparation was missing.

interface Line {
  key: string;
  kind: "food" | "product";
  id: string;
  name: string;
  grams: string;
}

const toLines = (r: BaseRecipeDto): Line[] =>
  r.lines.flatMap((l) =>
    l.kind === "base"
      ? []
      : [
          {
            key: crypto.randomUUID(),
            kind: l.kind,
            id: l.id,
            name: l.name,
            grams: String(l.grams).replace(".", ","),
          },
        ],
  );

export function BaseRecipeEditor({
  rid,
  name,
  onDone,
  onCancel,
}: {
  rid: string;
  name: string;
  onDone: (recipe: BaseRecipeDto, applied: number) => void;
  onCancel: () => void;
}) {
  const base = `/restaurants/${rid}/nutrition/base-recipes`;
  const [loading, setLoading] = useState(true);
  const [recipe, setRecipe] = useState<BaseRecipeDto | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [unfound, setUnfound] = useState<string[]>([]);
  const [edited, setEdited] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api<{ recipe: BaseRecipeDto; unfound: string[] }>("POST", `${base}/propose`, { name })
      .then((r) => {
        if (!alive) return;
        setRecipe(r.recipe);
        setLines(toLines(r.recipe));
        setUnfound(r.unfound);
      })
      .catch((e) => {
        // No proposal (AI unavailable or nothing found): the restaurant enters its own recipe.
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [base, name]);

  const change = (f: (ls: Line[]) => Line[]) => {
    setLines(f);
    setEdited(true);
  };

  async function save() {
    const parsed = lines.map((l) => ({ kind: l.kind, id: l.id, grams: Number(l.grams.replace(",", ".")) }));
    if (!parsed.length) {
      setError("Adaugă cel puțin un ingredient.");
      return;
    }
    if (parsed.some((l) => !(l.grams > 0 && l.grams <= 5000))) {
      setError("Fiecare ingredient are nevoie de un gramaj între 1 și 5000 g.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const standard = !edited && recipe?.source === "standard";
      const r = await api<{ recipe: BaseRecipeDto; applied: number }>("PUT", base, {
        name,
        lines: parsed,
        standard,
      });
      onDone(r.recipe, r.applied);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const total = lines.reduce((g, l) => g + (Number(l.grams.replace(",", ".")) || 0), 0);
  const confirmed = recipe?.status === "confirmed" && !edited;
  return (
    <div className="card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
      <div>
        <strong style={{ fontWeight: 600 }}>„{name}” e un preparat: din ce e făcut?</strong>
        <p className="caption muted" style={{ margin: "2px 0 0" }}>
          {loading
            ? "Caut o rețetă standard…"
            : recipe
              ? confirmed
                ? "Ai confirmat deja această rețetă. O poți folosi așa sau o poți schimba."
                : "Rețetă standard propusă. Confirm-o dacă se potrivește, sau schimbă ingredientele și gramajele după rețeta ta."
              : "Introdu rețeta ta: ingredientele și gramajele pentru o șarjă."}{" "}
          Contează proporțiile; la fiecare produs folosim cantitatea de {name} din rețeta produsului.
        </p>
      </div>
      {lines.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
          {lines.map((l, i) => (
            <li key={l.key} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ flex: "1 1 160px" }}>{l.name}</span>
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  className="field-input"
                  style={{ width: 84 }}
                  inputMode="decimal"
                  aria-label={`Gramaj ${l.name}`}
                  value={l.grams}
                  onChange={(e) =>
                    change((ls) => ls.map((x, j) => (j === i ? { ...x, grams: e.target.value } : x)))
                  }
                />
                <span className="muted">g</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-label={`Scoate ${l.name}`}
                  onClick={() => change((ls) => ls.filter((_, j) => j !== i))}
                >
                  <Trash2 {...ICON} aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
          <li className="caption muted">Total: {String(Math.round(total * 10) / 10).replace(".", ",")} g</li>
        </ul>
      )}
      {unfound.length > 0 && (
        <p className="caption muted" style={{ margin: 0 }}>
          Nu am găsit în baza de date: {unfound.join(", ")}. Adaugă-le mai jos dacă le folosești.
        </p>
      )}
      {!loading && (
        <FoodSearch
          rid={rid}
          id={`base-search-${name}`}
          label={`Adaugă un ingredient în ${name}`}
          query={query}
          onQuery={setQuery}
          onPick={(f) => {
            if (f.kind === "base") return;
            change((ls) => [
              ...ls,
              {
                key: crypto.randomUUID(),
                kind: f.kind as "food" | "product",
                id: f.id,
                name: f.name,
                grams: "100",
              },
            ]);
            setQuery("");
          }}
        />
      )}
      {error && <Alert tone="warning">{error}</Alert>}
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <button type="button" className="btn btn-primary btn-sm" disabled={loading || saving} onClick={save}>
          <Check {...ICON} aria-hidden="true" />{" "}
          {saving
            ? "Se salvează…"
            : !edited && recipe?.source === "standard" && recipe.status === "proposed"
              ? "Confirm rețeta standard"
              : confirmed
                ? "Folosește această rețetă"
                : "Salvează rețeta mea"}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Renunță
        </button>
      </div>
    </div>
  );
}
